import { type GridPosition, type SelectionRange } from '../../../state/types';
import { type SelectionAxis } from '../../types';

import { getRangeMinMax } from './get-range-min-max';

const getPositionAtIndex = (
  range: SelectionRange,
  targetIndex: number,
  axis: SelectionAxis
): GridPosition => {
  const anchorIndex = range.anchor[axis].index;
  return anchorIndex === targetIndex ? range.anchor : range.lead;
};

type MergeRangesParams = {
  range1: SelectionRange;
  range2: SelectionRange;
  newIndex: number;
  axis: SelectionAxis;
};

/**
 * Merges two selection ranges into a single contiguous range for the given axis.
 * Used when adding an index between two existing ranges.
 * @param range1 - First range to merge
 * @param range2 - Second range to merge
 * @param newIndex - The index being added between the ranges on the given axis
 * @param axis - The axis to merge ('row' or 'col')
 * @returns A new merged range using range1's ID
 */
export function mergeRanges({
  range1,
  range2,
  newIndex,
  axis
}: MergeRangesParams): SelectionRange {
  const r1 = getRangeMinMax(range1, axis);
  const r2 = getRangeMinMax(range2, axis);
  const newMin = Math.min(r1.min, r2.min, newIndex);
  const newMax = Math.max(r1.max, r2.max, newIndex);

  const leftRange = r1.min < r2.min ? range1 : range2;
  const rightRange = r1.min < r2.min ? range2 : range1;
  const leftMin = r1.min < r2.min ? r1.min : r2.min;
  const rightMax = r1.min < r2.min ? r2.max : r1.max;

  const leftPosition = getPositionAtIndex(leftRange, leftMin, axis);
  const newAnchor = { ...leftPosition, [axis]: { index: newMin } };

  const rightPosition = getPositionAtIndex(rightRange, rightMax, axis);
  const newLead = {
    ...rightPosition,
    [axis]: { index: newMax }
  };

  return {
    id: range1.id,
    anchor: newAnchor,
    lead: newLead
  };
}
